// Admin Pages - Simplified versions for MVP
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { tasksAPI, projectsAPI, membersAPI, formatApiErrorDetail } from '../../lib/api';
import { Plus, CheckSquare, Clock, Warning } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'];
const PRIORITIES = ['low', 'medium', 'high'];

const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', project_id: '', assigned_to: '',
    status: 'pending', priority: 'medium', due_date: '', notes: ''
  });

  const fetchData = async () => {
    try {
      const [tasksRes, projectsRes, membersRes] = await Promise.all([
        tasksAPI.getAll(),
        projectsAPI.getAll(),
        membersAPI.getAll()
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredTasks = filterStatus === 'all' ? tasks : tasks.filter(t => t.status === filterStatus);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await tasksAPI.create(formData);
      toast.success('Task created');
      setShowModal(false);
      setFormData({ title: '', description: '', project_id: '', assigned_to: '', status: 'pending', priority: 'medium', due_date: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const isOverdue = (dueDate) => dueDate && new Date(dueDate) < new Date();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tasks-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Tasks</h1>
            <p className="text-[#8A847A]">{tasks.length} total tasks</p>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="add-task-button">
            <Plus size={18} className="mr-2" /> New Task
          </Button>
        </div>

        <div className="flex gap-4 bg-white border border-[#D8D2C4] p-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 rounded-none">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <div className="bg-white border border-[#D8D2C4]">
            <table className="w-full data-table" data-testid="tasks-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Task</th>
                  <th className="px-4 py-3 text-left">Project</th>
                  <th className="px-4 py-3 text-left">Assigned To</th>
                  <th className="px-4 py-3 text-left">Due Date</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Priority</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(task => (
                  <tr key={task.id} data-testid={`task-row-${task.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <CheckSquare size={18} className={task.status === 'completed' ? 'text-green-600' : 'text-[#8A847A]'} />
                        <span className={task.status === 'completed' ? 'line-through text-[#8A847A]' : 'text-[#1A1614]'}>
                          {task.title}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#4A453E]">
                      {projects.find(p => p.id === task.project_id)?.title || '-'}
                    </td>
                    <td className="px-4 py-3 text-[#4A453E]">
                      {members.find(m => m.id === task.assigned_to)?.full_name || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {task.due_date ? (
                        <span className={`flex items-center gap-1 ${isOverdue(task.due_date) && task.status !== 'completed' ? 'text-red-600' : 'text-[#4A453E]'}`}>
                          {isOverdue(task.due_date) && task.status !== 'completed' && <Warning size={14} />}
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs capitalize ${
                        task.status === 'completed' ? 'bg-green-100 text-green-700' :
                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {task.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs capitalize ${
                        task.priority === 'high' ? 'bg-red-100 text-red-700' :
                        task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.priority}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredTasks.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8A847A]">No tasks found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">New Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="task-form">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required className="rounded-none" data-testid="task-input-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Project</label>
                  <Select value={formData.project_id} onValueChange={(v) => setFormData({...formData, project_id: v})}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None</SelectItem>
                      {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Assign To</label>
                  <Select value={formData.assigned_to} onValueChange={(v) => setFormData({...formData, assigned_to: v})}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Unassigned</SelectItem>
                      {members.map(m => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Priority</label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Due Date</label>
                  <Input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="rounded-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} className="rounded-none" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">Cancel</Button>
                <Button type="submit" className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="task-submit-button">Create Task</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TasksPage;
